// eslint-disable-next-line @nx/enforce-module-boundaries
import type { IMetaData } from '@marriott/mi-merch-components';
import { getStyleClassPerTheme, ImageAspectRatio } from '@marriott/mi-ui-library';
import {
  LayeredFeatureCfContent,
  LayeredFeatureCfPayload,
  LayeredFeatureDcaProps,
  LayeredFeatureDCAPayload,
  RenditionList,
} from './adapter.types';
import { getRecentlyViewedImageFallbackForPayload } from '../dynamicContentUtils';
import { resolveImageUrl, isExternalLink } from '../slotPropNormalizerUtils';
import { buildLayeredFeatureDcaRenditions, buildLayeredFeatureDynamicMediaNode } from '../dynamicMediaUtils';

function resolveOnlyTruthyImageUrls(imageUrls: Record<string, string | null>): Record<string, string> {
  return Object.fromEntries(Object.entries(imageUrls).map(([key, value]) => [key, resolveImageUrl(value ?? '')]));
}

function getCfDescription(cf: LayeredFeatureCfContent): string {
  const { description } = cf;
  if (typeof description === 'string') return description;
  if (description?.plaintext) return description.plaintext;
  return '';
}

export function adaptContentFragmentToLayeredFeature(ctx: LayeredFeatureCfPayload): LayeredFeatureDcaProps {
  const cf = ctx.contentObj;
  const header = cf.header ?? cf.title ?? '';
  const description = getCfDescription(cf);
  const className = cf.styleclass ?? cf.styleClass ?? '';
  const cfAssetPath = cf.image?.assetPath ?? cf.assetPath;
  const cfAltText = cf.image?.altText ?? cf.altText ?? ctx.alt;
  const dynamicMedia = buildLayeredFeatureDynamicMediaNode(cfAssetPath, cfAltText);
  const backgroundImage = {
    imageSrc: resolveImageUrl(dynamicMedia?.assetPath ?? ''),
    alternateDescription: dynamicMedia?.altText ?? '',
    title: cf.title ?? '',
    defaultImage: false,
    imageUrls: {},
    renditions: (dynamicMedia?.renditions ?? []) as RenditionList,
  };
  const mcomRegex = /^mcom-[^:]+:(?:brands?|general)\//;
  const isLogoBrandToken = !!cf.icon?.match(mcomRegex);
  const theme = getStyleClassPerTheme(cf?.theme) || undefined;
  const showEyebrow = !cf.icon;

  return {
    eyebrowText: showEyebrow ? cf.eyebrow ?? '' : '',
    headerLong: header,
    headerMedium: header,
    headerShort: header,
    descriptionLong: description,
    descriptionMedium: description,
    descriptionShort: description,
    callToActionText1: cf.primaryCTALinkText || cf.primaryCtaLinkText,
    destinationUrl1: cf.primaryCTALink || cf.primaryCtaLink,
    callToActionSize1: cf.primaryCtaSize,
    callToActionSize2: cf.secondaryCtaSize,
    callToActionText2: cf.secondaryCtaLinkText,
    destinationUrl2: cf.secondaryCtaLink,
    openInNewTab1: cf.primaryCtaOpenInNewTab,
    openInNewTab2: cf.secondaryCtaOpenInNewTab,
    primaryCtaAriaLabel: cf.primaryCtaAriaLabel,
    secondaryCtaAriaLabel: cf.secondaryCtaAriaLabel,
    tertiaryLinkText: cf.tertiaryCtaLinkText ?? '',
    tertiaryLinkUrl: cf.tertiaryCtaLink ?? '',
    tertiaryOpenInANewTab: cf.tertiaryOpenInANewTab ?? false,
    tertiaryLinkAriaLabel: cf.tertiaryLinkAriaLabel ?? '',
    backgroundImage,
    desktopVariation: cf.desktopVariation ?? ImageAspectRatio.SIZE_3x1,
    tabletVariation: cf.tabletVariation ?? ImageAspectRatio.SIZE_2X1,
    mobileVariation: cf.mobileVariation ?? ImageAspectRatio.SIZE_1x1,
    className,
    logo: {
      imageSrc: isLogoBrandToken ? cf.icon || '' : resolveImageUrl(cf.icon ?? ''),
      title: '',
      alternateDescription: cf.logoImageAltText ?? '',
      defaultImage: false,
      imageUrls: {},
      renditions: [],
    },
    componentId: cf.componentId,
    caption: cf.photoCaption ?? '',
    captionUrl: cf.photoCaptionUrl ?? '',
    theme,
    type: 'cf' as const,
  };
}

export function adaptDcaLayeredFeature(ctx: LayeredFeatureDCAPayload): LayeredFeatureDcaProps {
  const dca = ctx.contentObj;
  const recentlyViewedRectangleFallback = getRecentlyViewedImageFallbackForPayload(
    dca as Record<string, unknown>,
    'rectangle'
  );
  const recentlyViewedStandardFallback = getRecentlyViewedImageFallbackForPayload(
    dca as Record<string, unknown>,
    'standardHorizontal'
  );
  const recentlyViewedPanoramicFallback = getRecentlyViewedImageFallbackForPayload(
    dca as Record<string, unknown>,
    'panoramicHorizontal'
  );
  const recentlyViewedSquareFallback = getRecentlyViewedImageFallbackForPayload(
    dca as Record<string, unknown>,
    'square'
  );
  const backgroundImageUrls = resolveOnlyTruthyImageUrls({
    ...(dca.image?.imageUrls ?? {}),
    ...(recentlyViewedPanoramicFallback ? { panoramicHorizontal: recentlyViewedPanoramicFallback } : {}),
    ...(recentlyViewedStandardFallback ? { standardHorizontal: recentlyViewedStandardFallback } : {}),
    ...(recentlyViewedSquareFallback ? { square: recentlyViewedSquareFallback } : {}),
  });
  const backgroundImageSrc = resolveImageUrl(dca.image?.imageSrc ?? recentlyViewedRectangleFallback ?? '');
  const backgroundImageRenditions = buildLayeredFeatureDcaRenditions(
    backgroundImageUrls,
    backgroundImageSrc
  ) as RenditionList;
  const metadata = (dca.metadata ?? (dca as Record<string, unknown>)['metaData']) as IMetaData | undefined;
  const iconRegex = /^(?:mcom-[^:]+:(?:brands)\/|marriott-icons:.+)/i;
  const isLogoBrandToken = !!dca.logo?.imageSrc?.match(iconRegex);
  const showEyebrow = !dca.logo?.imageSrc;

  return {
    eyebrowText: showEyebrow ? dca.eyebrowText ?? '' : '',
    headerMedium: dca.headerMedium,
    headerShort: dca.headerShort,
    descriptionLong: dca.descriptionLong,
    descriptionMedium: dca.descriptionMedium,
    descriptionShort: dca.descriptionShort,
    callToActionText1: dca.callToActionText1,
    destinationUrl1: dca.destinationUrl1,
    openInNewTab1: isExternalLink(dca.destinationUrl1 ?? '', false),
    callToActionText2: dca.callToActionText2,
    destinationUrl2: dca.destinationUrl2,
    openInNewTab2: isExternalLink(dca.destinationUrl2 ?? '', false),
    primaryCtaAriaLabel: dca.primaryCtaAriaLabel,
    secondaryCtaAriaLabel: dca.secondaryCtaAriaLabel,
    backgroundImage: {
      imageUrls: backgroundImageUrls,
      imageSrc: backgroundImageSrc,
      title: dca.image?.title ?? '',
      alternateDescription: dca.image?.alternateDescription ?? '',
      defaultImage: dca.image?.defaultImage ?? false,
      renditions: backgroundImageRenditions,
    },
    desktopVariation: ImageAspectRatio.SIZE_3x1,
    tabletVariation: ImageAspectRatio.SIZE_2X1,
    mobileVariation: ImageAspectRatio.SIZE_1x1,
    logo: {
      imageSrc: isLogoBrandToken ? dca.logo?.imageSrc || '' : resolveImageUrl(dca.logo?.imageSrc ?? ''),
      title: dca.logo?.title ?? '',
      alternateDescription: dca.logo?.alternateDescription ?? '',
      defaultImage: dca.logo?.defaultImage ?? false,
      imageUrls: resolveOnlyTruthyImageUrls(dca.logo?.imageUrls ?? {}),
      renditions: [],
    },
    caption: dca.caption ?? dca.photoCaption ?? '',
    captionUrl: dca.captionUrl ?? '',
    theme: getStyleClassPerTheme(metadata?.theme) || undefined,
    type: 'dca' as const,
    mboxParameter: dca.mboxName ?? (dca as { mboxParameter?: string }).mboxParameter,
    metadata,
    isTargetContentError: (dca as { isTargetContentError?: boolean }).isTargetContentError,
    isTargetContentLoaded: dca.isTargetContentLoaded ?? true,
    mathType: dca.mathType ?? undefined,
    mathLabel1: dca.mathLabel1 ?? undefined,
    mathLabel2: dca.mathLabel2 ?? undefined,
    mathLabel3: dca.mathLabel3 ?? undefined,
    mathValue: dca.mathValue ?? undefined,
    mathTitle: dca.mathTitle ?? undefined,
    legalLong: dca.legalLong ?? undefined,
  };
}
